import {
  FileIcon,
  FormIcon,
  GanttIcon,
  ListIcon,
  PinFilledIcon,
  PinIcon,
  StarFilledIcon,
  StarIcon,
  WhiteBoardIcon,
  WorksheetIcon,
} from '@sketchnote/icons';
import { Avatar, AvatarGroup, MoreActionMenu } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import * as dateFns from 'date-fns';
import { Emoji } from 'emoji-mart';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import Analytics from 'src/api/services/analytics/analytics';
import { useWorkspaceAccess } from 'src/components/workspaces/hooks/useWorkspaceAccess';

import { fileIconList } from '../assets/data';
import { FileListProps } from '../routes/Files';

const { useCallback, useEffect, useState, useMemo } = React;
const { parseISO, formatRelative } = dateFns;

export const FileListItem = ({
  name,
  icon,
  modified,
  onClick,
  sharedWith,
  menuItems,
  onClickMenuItem,
  className,
  onPinFile,
  onFavoriteFile,
  pinned,
  favorite,
  type,
  view,
  id,
  workspace,
  showWorkspaceDetails = false,
  showMenu = true,
  primaryViewId,
  titleClassName,
  avatarMaxCount = 3,
}: FileListProps): JSX.Element => {
  const router = useRouter();
  const orgId = router.query.orgId as string;
  const primaryView = useMemo(() => (type === 'board' ? view : type), [type, view]);
  const [pin, setPin] = useState(false);
  const [favoritefile, setFavoriteFile] = useState(false);
  const handleKeyEnter: React.KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.key === 'Enter') {
      onClick?.({ fileId: id, viewId: primaryViewId, type: type, view: view });
    }
  }, []);

  const { isViewer } = useWorkspaceAccess();

  function handleOnClick() {
    onClick?.({ fileId: id, viewId: primaryViewId, type: type.toLowerCase(), view: view });
  }

  function handleMoreMenuClick(item, e) {
    onClickMenuItem?.(item, e, id, name);
  }

  useEffect(() => {
    typeof pinned === 'boolean' && setPin(pinned);
  }, [pinned]);

  useEffect(() => {
    typeof favorite === 'boolean' && setFavoriteFile(favorite);
  }, [favorite]);

  return (
    <div
      className={classNames({
        'flex w-full justify-between border px-24 py-12 rounded-md hover:shadow-md transition-all duration-300 bg-white':
          true,
        [className || '']: !!className,
      })}
      tabIndex={0}
      id="snCreationComponent"
      aria-label="Document"
      title={`${name}`}
      role="button"
      onKeyDown={handleKeyEnter}
      onClick={handleOnClick}
    >
      <div className="flex items-center docFileInfoDiv">
        <div className={classNames('mr-16 w-22 h-22 items-center')}>
          {icon?.id && icon?.type !== 'image' ? (
            <Emoji emoji={icon?.id} set="apple" size={22} />
          ) : icon?.id && icon?.type === 'image' ? (
            <img className=" object-center object-cover w-22 h-22" src={icon?.id} />
          ) : (
            <span className="bg-gray-100 rounded-sm m-auto items-center">
              <FileIcon className=" text-primary-500 rounded-sm block text-xl" />
            </span>
          )}
        </div>
        <div
          className={
            (classNames({
              '': true,
            }),
            'DocFileTitleDiv')
          }
        >
          <div>
            <div className="flex leading-20 align-middle items-center -ml-1">
              <p className={`mr-8 mb-4 font-medium contain overflow-auto ${titleClassName}`}>
                {name || 'Untitled'}
              </p>
            </div>

            <p className="text-gray-500 text-xs flex ">
              <Tippy
                className="w-auto h-auto bg-gray-999"
                content={`Primary view: ${
                  primaryView?.charAt(0)?.toUpperCase() + primaryView?.slice(1)
                }`}
                arrow={true}
              >
                <>
                  <span className="inline-block text-blue-500 mt-1">
                    {fileIconList[primaryView]}
                  </span>
                  <span className="inline-block text-blue-500 font-medium">
                    &nbsp;
                    {primaryView?.charAt(0)?.toUpperCase() + primaryView?.slice(1)}
                  </span>
                </>
              </Tippy>

              <span className="mobile:hidden laptop:flex">
                <span className="px-5 text-gray-300">|</span>
                {showWorkspaceDetails && (
                  <span className="inline-block hover:underline mobile:hidden laptop:flex">
                    <span className="max-w-320">
                      {workspace?.id ? (
                        <Link href={`/w/${orgId}/w/${workspace?.id}`}>
                          <a onClick={(e) => e.stopPropagation()} className="max-w-105 truncate">
                            <span className="inline-block max-w-90 truncate">
                              {' '}
                              In: {workspace?.name}
                            </span>
                          </a>
                        </Link>
                      ) : sharedWith && sharedWith?.length > 0 ? (
                        <Link href={`/w/${orgId}/files?list=shared}`}>
                          <a onClick={(e) => e.stopPropagation()}>
                            <span>In shared</span>
                          </a>
                        </Link>
                      ) : (
                        <Link href={`/w/${orgId}/files?list=private`}>
                          <a>
                            <span>In private</span>
                          </a>
                        </Link>
                      )}
                    </span>
                    <span className="px-5 text-gray-300">|</span>
                  </span>
                )}

                {`Edited ${
                  modified?.timing ? formatRelative(parseISO(modified?.timing), new Date()) : ''
                }`}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center mobile:hidden laptop:flex">
        {!router.query.workspaceId && (
          <div className="flex items-center mr-4">
            {sharedWith?.length ? (
              <div className="flex">
                <AvatarGroup maxCount={avatarMaxCount}>
                  {sharedWith?.map((item) => (
                    <Avatar
                      name={item.member.firstname}
                      onClick={(e) => e.stopPropagation()}
                      src={item.member.avatar}
                      type="photo"
                      key={item.member.firstname}
                      wrapperClassName="-mr-8 border-2 border-white transform transition duration-500 hover:scale-125"
                    />
                  ))}
                </AvatarGroup>
                {sharedWith?.length > avatarMaxCount && (
                  <Avatar
                    name={sharedWith?.length - avatarMaxCount}
                    tooltip={false}
                    type={'initials'}
                    src={''}
                    wrapperClassName="-ml-6 border-1 border-white  transform transition duration-500 hover:scale-125"
                  />
                )}
              </div>
            ) : null}
          </div>
        )}

        <button
          className="mx-12 -mt-1"
          onClick={(e) => {
            e.stopPropagation();
            Analytics.track(`File ${!favoritefile ? 'marked favorite' : 'unmarked favorite'}`, {
              fileName: name || 'Untitled',
            });
            onFavoriteFile?.(id, !favoritefile, name || 'File');
            setFavoriteFile(!favoritefile);
          }}
        >
          {favoritefile ? (
            <StarFilledIcon className="text-yellow-500 mt-6" />
          ) : (
            <StarIcon className="text-gray-500" />
          )}
        </button>

        <button
          className=""
          onClick={(e) => {
            e.stopPropagation();
            Analytics.track(`File ${pin ? 'unpinned' : 'pinned'}`, {
              fileName: name || 'Untitled',
            });
            onPinFile?.(id, !pin, name || 'File');
            setPin(!pin);
          }}
        >
          {pin ? (
            <PinFilledIcon className="text-gray-500" />
          ) : (
            <PinIcon className="text-gray-500" />
          )}
        </button>

        {!isViewer && menuItems && showMenu && (
          <div className="flex items-center text-gray-500">
            <MoreActionMenu
              menuItems={menuItems}
              onClickMenuItem={handleMoreMenuClick as any}
              placement="bottom"
              classNames={{ labelWrap: 'px-12' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
